import PropTypes from 'prop-types';
import './style.scss';

import { useMemo, useEffect, useState } from 'react';

import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useTranslation } from 'react-i18next';
import { getProjectById } from 'apis/projects.api';

import { getFinancialAdjustmentById } from 'apis/financialAdjustment.api';

import { TOAST } from 'constant';
import { toast } from 'layout';

import Loading from 'components/Loading';
import { adjustmentCommentsSchema } from 'features/SubmitProjectProposal/validation';
import FinancialAdjustmentForm from '../../components/FinancialAdjustmentForm';

function EditFinancialAdjustment({ id, readOnly, isSumUp }) {
  // #region Data
  const { t } = useTranslation();
  const { projectId, financialAdjustmentId } = useParams();

  const financialAdjustmentIdUsed = financialAdjustmentId || id;

  const [schema, setSchema] = useState(adjustmentCommentsSchema);

  const { control, handleSubmit, watch, setValue, getValues, reset } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { errors, isDirty, dirtyFields, isValid } = useFormState({
    control,
  });

  const { data: projectData, isLoading: isProjectDataLoading } = useQuery(
    ['project', projectId],
    () => getProjectById(projectId)
  );
  const project = useMemo(() => projectData?.data, [projectData]);

  const {
    data: financialAdjustmentData,
    isLoading: isFinancialAdjustmentDataLoading,
    refetch,
  } = useQuery(['financialAdjustment', financialAdjustmentIdUsed], () =>
    getFinancialAdjustmentById(financialAdjustmentIdUsed)
  );
  const financialAdjustment = useMemo(
    () => financialAdjustmentData?.data,
    [financialAdjustmentData]
  );

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };
  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  useEffect(() => {
    setValue('commitment', financialAdjustment?.commitment);
    setValue('otherExpenditure', financialAdjustment?.otherExpenditure || 0);
    setValue(
      'financialAdjustmentItems',
      financialAdjustment?.financialAdjustmentItems?.map(
        ({ proposedExpenditureType, ...rest }) => ({
          ...rest,
          name: proposedExpenditureType?.name,
          subProposedExpenditureTypeId: proposedExpenditureType?.subProposedExpenditureTypeId,
        })
      )
    );
  }, [JSON.stringify(financialAdjustment)]);

  if (isProjectDataLoading || isFinancialAdjustmentDataLoading) {
    return <Loading />;
  }

  return (
    <div className={`${!id && 'financial-adjustment-container'}`}>
      <FinancialAdjustmentForm
        control={control}
        errors={errors}
        watch={watch}
        reset={reset}
        refetch={refetch}
        setValue={setValue}
        getValues={getValues}
        handleSubmit={handleSubmit}
        checkValidation={checkValidation}
        checkKeyDown={checkKeyDown}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        footerConfirmationPrompt={footerConfirmationPrompt}
        project={project}
        readOnly={!!financialAdjustment?.submittedAt || readOnly}
        financialAdjustmentId={financialAdjustmentIdUsed}
        financialAdjustment={financialAdjustment}
        hasContractFile={!project?.fileUrl}
        setSchema={setSchema}
        isSumUp={isSumUp}
      />
    </div>
  );
}

EditFinancialAdjustment.propTypes = {
  id: PropTypes.string,
  readOnly: PropTypes.bool,
  isSumUp: PropTypes.bool,
};

EditFinancialAdjustment.defaultProps = { id: null, readOnly: false, isSumUp: false };

export default EditFinancialAdjustment;
